import {useCallback} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/sharp-regular-svg-icons/faClose";
import {FCWithHTMLAttributes} from "@/app/types/reactTypes";
import TextField, {TextFieldClasses} from "@/app/_components/forms/TextField";
import Input from "@/app/_components/forms/Input";
import useKeyArray from "@/app/util/useKeyArray";
import clsMerge from "@/app/util/clsMerge";

type DynamicFieldProps = {
	classes?: {
		listRoot?: string;
		addButton?: string;
		fields?: TextFieldClasses;
	};
	fieldData?: Record<number, string>;
	fieldName: string;
	label: string | ((index: number) => string);
	placeholder?: string | ((index: number) => string);
	onFieldChange?: (key: number, value: string) => void;
	isEachRequired?: boolean;
};

const DynamicListField: FCWithHTMLAttributes<DynamicFieldProps> = ({
	children,
	classes,
	fieldData = {},
	fieldName,
	label,
	placeholder,
	onFieldChange,
	isEachRequired,
}) => {
	// setup a key array for the fields
	const [
		additionalFields,
		{push: pushAdditionalField, removeAt: removeFieldAt},
	] = useKeyArray({initialValues: Object.keys(fieldData)});

	// updater function
	const onInputChange = useCallback(
		(key: number, value: string) => {
			onFieldChange?.(key, value);
		},
		[onFieldChange]
	);

	return (
		<>
			{additionalFields.map((key, index) => (
				<div
					key={key}
					className={clsMerge("flex flex-row gap-2", classes?.listRoot)}
				>
					<TextField
						name={fieldName}
						labelText={typeof label === "string" ? label : label(index)}
						type="text"
						isRequired={isEachRequired}
						classes={{
							root: "flex-grow",
							...classes?.fields,
						}}
					>
						<Input
							autoFocus={index === additionalFields.length - 1}
							placeholder={
								typeof placeholder === "string"
									? placeholder
									: placeholder?.(index)
							}
							value={fieldData?.[key]}
							onChange={(e) => {
								onInputChange(key, e.target.value);
							}}
						/>
					</TextField>
					<button
						className="mt-7 flex h-10 w-10 items-center justify-center rounded hover:bg-gray-200 hover:bg-opacity-30"
						type="button"
						aria-label={`Remove ${typeof label === "string" ? label : label(index)}`}
						onClick={(e) => {
							e.preventDefault();
							removeFieldAt(index);
							onInputChange(key, "");
						}}
					>
						<FontAwesomeIcon icon={faClose} />
					</button>
				</div>
			))}
			<button
				className={classes?.addButton}
				type="button"
				onClick={(e) => {
					e.preventDefault();
					pushAdditionalField();
				}}
			>
				{children}
			</button>
		</>
	);
};

export default DynamicListField;
